export default class StreamPlayer {
  constructor(streamUrl, bufferWindow = 30) {
    if (!streamUrl) throw new Error('StreamPlayer requires a stream URL');

    this.STREAM_URL = streamUrl;
    this.BUFFER_WINDOW = bufferWindow;

    this.audio = document.createElement('audio');
    this.audio.setAttribute('playsinline', '');
    this.audio.setAttribute('preload', 'none');

    this.mediaSource = null;
    this.sourceBuffer = null;
    this.reader = null;
    this.abortController = null;
    this.chunkQueue = [];

    this.isPausedByUser = false;

    // reconnect backoff
    this.reconnectBase = 3000; // 3s
    this.reconnectMax = 30000; // 30s
    this.reconnectDelay = this.reconnectBase;
    this.reconnectTimer = null;

    // watchdog (detects frozen playback)
    this.watchdogTimer = null;
    this.lastTime = 0;
    this.STALL_CHECK_MS = 6000;  // check every 6s
    this.STALL_TIMEOUTS = 2;     // 2 consecutive checks (~12s) = stall
    this.stallCount = 0;

    // track successful appends to reset backoff on good flow
    this.lastAppendAt = 0;

    // bind for event cleanup
    this.onSourceClose = this.onSourceClose.bind(this);
    this.onSourceEnded = this.onSourceEnded.bind(this);

    this.events = {};

    // Proxy some native audio events
    ["play", "pause", "volumechange", "timeupdate"].forEach(evt => {
      this.audio.addEventListener(evt, e => this.emit(evt, e));
    });

    //fallback detection
    this.useFallback = (typeof window === 'undefined') || !window?.MediaSource?.isTypeSupported?.('audio/mpeg');
    this.codec = this.useFallback ? null : 'audio/mpeg';
  }

  async startStream() {
    if (this.mediaSource) return;
    this.cleanup(); // defensive

    if (this.useFallback) {
      // Fallback mode: plain <audio> streaming
      this.audio.src = this.STREAM_URL;
      this.audio.load();
      this.emit("fallback", { reason: "no MSE codec support" });
      this.startWatchdog();
      return;
    }

    this.mediaSource = new MediaSource();
    this.audio.src = URL.createObjectURL(this.mediaSource);

    this.mediaSource.addEventListener('sourceopen', async () => {
      if (!this.mediaSource || this.mediaSource.readyState !== 'open') return;

      try {
        this.sourceBuffer = this.mediaSource.addSourceBuffer(this.codec);
      } catch (e) {
        console.error('addSourceBuffer failed');
        this.scheduleReconnect();
        return;
      }

      this.emit("codec", this.codec);

      this.mediaSource.addEventListener('sourceclose', this.onSourceClose);
      this.mediaSource.addEventListener('sourceended', this.onSourceEnded);

      this.abortController = new AbortController();

      try {
        const response = await fetch(this.STREAM_URL, { signal: this.abortController.signal });
        if (!response.body) throw new Error('Stream not supported by fetch body');

        this.reader = response.body.getReader();
        this.resetBackoff();
        this.startWatchdog();
        this.emit("connected");
        this.readChunks();
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.emit("error", err);
          this.scheduleReconnect();
        }
      }
    }, { once: true });
  }

  async readChunks() {
    try {
      while (this.reader) {
        const { done, value } = await this.reader.read();

        // cleanup may have happened while awaiting .read()
        if (!this.reader) break;

        if (done) {
          this.emit("ended");
          break;
        }

        if (!value || value.length === 0) continue;

        this.chunkQueue.push(value);
        this.processQueue();
      }

      // Reader loop exited -> reconnect unless user paused
      if (!this.isPausedByUser) {
        this.scheduleReconnect();
      }

    } catch (err) {
      if (err.name !== 'AbortError') {
        this.emit("error", err);
        console.error("Stream read error");
        this.scheduleReconnect();
      }
    }
  }

  processQueue() {
    if (!this.sourceBuffer ||
        !this.mediaSource ||
        this.mediaSource.readyState !== 'open' ||
        this.sourceBuffer.updating ||
        this.chunkQueue.length === 0) return;

    const localBuffer = this.sourceBuffer; // guard for cleanup

    // drop old chunks if queue is too long
    const MAX_QUEUE_CHUNKS = 50; // adjust depending on network and chunk size
    if (this.chunkQueue.length > MAX_QUEUE_CHUNKS) {
      const dropCount = this.chunkQueue.length - MAX_QUEUE_CHUNKS;
      this.chunkQueue.splice(0, dropCount);
    }

    const chunk = this.chunkQueue.shift();
    try {
      localBuffer.appendBuffer(chunk);
    } catch (err) {
      this.emit("error", err);
      //this.chunkQueue.unshift(chunk); // retry next loop
      this.scheduleReconnect();
      return;
    }

    localBuffer.addEventListener('updateend', () => {
      if (!this.sourceBuffer || this.sourceBuffer !== localBuffer) return;
      if (!this.mediaSource || this.mediaSource.readyState !== 'open') return;

      this.lastAppendAt = performance.now();
      this.resetBackoff();
      this.trimBuffer();

      // schedule next append, letting the event loop breathe
      if (this.chunkQueue.length > 0) queueMicrotask(() => this.processQueue());
    }, { once: true });
  }

  trimBuffer() {
    const sb = this.sourceBuffer;
    if (!sb || !this.mediaSource || this.mediaSource.readyState !== 'open' || sb.updating) return;
    //if (!this.sourceBuffer || this.sourceBuffer.updating) return;
    let buffered;
    try {
      buffered = sb.buffered;
    } catch (e) { return; }

    if (!buffered || buffered.length === 0) return;

    const bufferStart = buffered.start(0);
    const bufferEnd = buffered.end(buffered.length - 1);
    const trimTarget = bufferEnd - this.BUFFER_WINDOW;

    if (trimTarget > bufferStart && !sb.updating) {
      try {
        sb.remove(bufferStart, trimTarget);
      } catch (err) {
        // non-fatal; next cycle will try again
      }
    }
  }

  // ---- Playback control ----
  async play() {
    this.isPausedByUser = false;

    if (this.useFallback) {
      this.audio.src = this.STREAM_URL;
      this.audio.load();
      this.audio.play().catch(() => {});
      this.startWatchdog();
      return;
    }

    this.cleanup();

    return this.startStream()
      .then(() => this.audio.play()
        .catch(() => {})
      );
  }

  pause() {
    this.isPausedByUser = true;
    this.audio.pause();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopWatchdog();
  }

  setVolume(v) {
    this.audio.volume = Math.max(0, Math.min(1, v));
  }

  // ---- Reconnect/backoff/watchdog ----
  scheduleReconnect() {
    if (this.isPausedByUser) return;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

    const delay = this.reconnectDelay;
    console.warn(`Reconnecting in ${Math.round(delay / 1000)}s...`);
    this.emit("reconnecting", this.reconnectDelay);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isPausedByUser) {
        this.useFallback ? this.fallbackReconnect() : this.startStream();
      }
    }, delay);

    // exponential backoff with cap
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.reconnectMax);
  }

  fallbackReconnect() {
    if (this.isPausedByUser) return;
  
    this.audio.src = this.STREAM_URL;
    this.audio.load();
    this.audio.play().catch(() => {});
  }

  resetBackoff() {
    this.reconnectDelay = this.reconnectBase;
  }

  startWatchdog() {
    this.stopWatchdog();
    this.lastTime = this.audio.currentTime;
    this.stallCount = 0;

    this.watchdogTimer = setInterval(() => {
      if (this.isPausedByUser || this.audio.paused) return;

      const nowTime = this.audio.currentTime;
      if (Number.isFinite(nowTime) && nowTime > this.lastTime + 0.05) {
        // progressed
        this.lastTime = nowTime;
        this.stallCount = 0;
        return;
      }

      // no progress this tick
      this.stallCount += 1;
      if (this.stallCount >= this.STALL_TIMEOUTS) {
        this.stallCount = 0;
        this.scheduleReconnect();
      }
    }, this.STALL_CHECK_MS);
  }

  stopWatchdog() {
    if (this.watchdogTimer) clearInterval(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  // ---- MediaSource lifecycle ----
  onSourceClose() {
    // When source closes unexpectedly, try to reconnect (unless paused)
    if (!this.isPausedByUser) this.scheduleReconnect();
  }

  onSourceEnded() {
    if (!this.isPausedByUser) this.scheduleReconnect();
  }

  // ---- Cleanup ----
  cleanup() {
    // stop reconnect timer
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;

    // stop watchdog
    this.stopWatchdog();

    // nullify reader first to prevent readChunks from reconnecting
    const reader = this.reader;
    this.reader = null;

    // abort fetch after reader is null
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // cleanup MediaSource / SourceBuffer
    if (this.mediaSource) {
      try { this.mediaSource.removeEventListener('sourceclose', this.onSourceClose); } catch {}
      try { this.mediaSource.removeEventListener('sourceended', this.onSourceEnded); } catch {}
      try { if (this.mediaSource.readyState === 'open') this.mediaSource.endOfStream(); } catch {}
    }

    this.sourceBuffer = null;
    this.mediaSource = null;

    // cleanup audio element
    if (this.audio.src) {
      if (this.audio.src.startsWith("blob:")) {
        try { URL.revokeObjectURL(this.audio.src); } catch {}
      }
      this.audio.removeAttribute('src');
      this.audio.load();
    }

    // clear queued chunks
    this.chunkQueue = [];
  }

  destroy() {
    this.pause();
    this.cleanup();
    this.emit("destroyed");
    this.events = {};
  }

  // --- A few simple getters ----
  get element() { return this.audio; }

  get isPlaying() { return !this.audio.paused && !this.isPausedByUser; }

  get volume() { return this.audio.volume; }

  // --- Event system ---
  // using set to prevent duplicate handlers (tho can be done with arrays + exists check)
  on(event, handler) {
    (this.events[event] ??= new Set()).add(handler);
  }

  off(event, handler) {
    this.events[event]?.delete(handler);
  }

  emit(event, data) {
    if (!this.events[event]) return;
    for (const h of this.events[event]) h(data);
  }
}